import type { AxiosInstance, AxiosResponse } from 'axios';
import type { Tenant } from '../types';
import {
  configureApiClient,
  getApiClient,
  type TokenStorage,
  type AuthEvents,
} from '@offertify/api-client';
import { clearWebOfflineCaches } from './offlineStorage';
import { LoginRequest, LoginResponse, ApiResponse, PaginatedResponse, Offerta, CreateOffertaRequest, Template, Prodotto, ProdottoImmagine, AuditLog, AuditLogFilters, AuditLogStats, FICQuoteListResponse, User, UserBrief, UserRole, UserCreateRequest, UserUpdateRequest, PasswordChangeRequest, PasswordResetRequest, ForgotPasswordRequest, ResetPasswordRequest, EmailChangeRequest, EmailChangeConfirmRequest, RolesPermissionsResponse, OffertaStats, Listino, ListinoListItem, ListinoPrezzo, ListinoPrezzoUpsert, BulkPriceUpdate, Promozione, PromozioneCliente, PromozioneUtilizzoReportItem, PriceResolution, AutorizzazioneSconto, CatalogProduct, CustomerProfile, CustomerOrderCreate, CustomerOrderUpdate, ChatSession, ChatSessionDetail, WmsConnectionStatus, WmsMapping, WmsStockInfo, WmsAutoMatchResult, WmsOrderStatus, SyncDaemonNode, SyncDispatchJob, SyncQueryDefinition, SyncTenantAccess, StorageConfigStatus, StorageProvider } from '../types';
import type {
  LeadResponse,
  LeadBrief,
  LeadCreate,
  LeadUpdate,
  LeadFilters,
  LeadListResponse,
  LeadActivityCreate,
  LeadActivityListResponse,
  LeadActivityResponse,
  LeadAssignRequest,
  LeadConvertRequest,
  LeadConvertResponse,
  LeadConvertPreview,
  LeadStats,
  LeadPipelineResponse,
} from '../types/lead';
import type {
  LeadIntakeFormCreate,
  LeadIntakeFormUpdate,
  LeadIntakeFormResponse,
  LeadIntakeFormListResponse,
  LeadIntakeFormStats,
  RotateSlugResponse,
} from '../types/leadIntakeForm';
import type {
  LeadTagCreate,
  LeadTagUpdate,
  LeadTagResponse,
  LeadTagListResponse,
  LeadTagAttachResponse,
} from '../types/leadTag';
import type {
  ActivityTypeContext,
  ActivityTypeCreate,
  ActivityTypeUpdate,
  ActivityTypeResponse,
  ActivityTypeListResponse,
} from '../types/activityType';
import type {
  LeadScoreRuleCreate,
  LeadScoreRuleUpdate,
  LeadScoreRuleResponse,
  LeadScoreRuleListResponse,
  ScoreChangeResponse,
  RecomputeAllResponse,
  ToggleResponse,
} from '../types/leadScoreRule';
import type {
  LeadSegmentCreate,
  LeadSegmentUpdate,
  LeadSegmentResponse,
  LeadSegmentListResponse,
  LeadSegmentPreview,
} from '../types/leadSegment';
import type {
  LeadCampaignCreate,
  LeadCampaignUpdate,
  LeadCampaignResponse,
  LeadCampaignListResponse,
  LeadCampaignMetrics,
  LeadCampaignDeliveryListResponse,
  LeadCampaignStepCreate,
  LeadCampaignStepUpdate,
  LeadCampaignStepResponse,
  PreviewStepResponse,
  CampaignState,
  DeliveryFilterState,
} from '../types/leadCampaign';
import type {
  PromozioneAllegatoResponse,
  TenantMeResponse,
  TenantUpdateMeRequest,
} from '../types/promozioneAllegato';
import type {
  OpenapiValidatePiRequest,
  OpenapiValidatePiResponse,
  OpenapiSearchRequest,
  OpenapiSearchResponse,
  OpenapiVisuraRequest,
  OpenapiVisuraTicket,
  OpenapiVisuraStatusResponse,
  OpenapiQuotaResponse,
  OpenapiUpgradeRequestCreate,
  OpenapiUpgradeRequestResponse,
  OpenapiUpgradeRequestListResponse,
  OpenapiUpgradeRequestState,
  OpenapiHostUpgradeRequestResponse,
  OpenapiHostUpgradeRequestListResponse,
  OpenapiHostReviewRequest,
  OpenapiHostUsageResponse,
  OpenapiCompanyListResponse,
  OpenapiCompanyRead,
  OpenapiCacheStatsResponse,
  OpenapiSettingsRead,
  OpenapiSettingsUpdate,
  OpenapiSettingsTestRequest,
  OpenapiSettingsTestResponse,
} from '../types/openapi';

const API_BASE_URL = (import.meta.env.DEV && import.meta.env.VITE_API_URL) || (window as any).CONFIG?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

export interface OfferNumberingSettings {
  pattern: string;
  anno: number;
  prossimo_seq: number;
  esempio: string;
}

// --- Shared transport (@offertify/api-client) with web platform adapters ---
// The interceptor logic (bearer token + refresh-on-401) now lives in the shared
// APIClient. Web injects a localStorage-backed TokenStorage and a redirect-based
// AuthEvents so the behavior is byte-for-byte identical to before.

const webTokenStorage: TokenStorage = {
  getAccess: async () => localStorage.getItem('access_token'),
  getRefresh: async () => localStorage.getItem('refresh_token'),
  setTokens: async (access, refresh) => {
    localStorage.setItem('access_token', access);
    if (refresh) {
      localStorage.setItem('refresh_token', refresh);
    }
  },
  clear: async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Session is over (e.g. refresh failed) — drop the offline visite snapshot too.
    await clearWebOfflineCaches();
  },
};

const webAuthEvents: AuthEvents = {
  onUnauthorized: () => {
    window.location.href = '/login';
  },
};

// Configure the shared singleton once; every endpoint below reuses this transport.
configureApiClient({
  baseURL: API_BASE_URL,
  storage: webTokenStorage,
  authEvents: webAuthEvents,
});

class APIClient {
  public client: AxiosInstance = getApiClient().client;

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AxiosResponse<ApiResponse<LoginResponse>>> {
    return this.client.post('/auth/login', credentials);
  }

  async logout(refresh_token?: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.post('/auth/logout', { refresh_token });
  }

  async refreshToken(refresh_token: string): Promise<AxiosResponse<ApiResponse<LoginResponse>>> {
    return this.client.post('/auth/refresh', { refresh_token });
  }

  // Offers endpoints
  async getOffers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    stato?: string;
    as400_status?: string;
    cliente_id?: string;
    anno?: number;
  }): Promise<AxiosResponse<PaginatedResponse<Offerta>>> {
    return this.client.get('/offerte', { params });
  }

  async getOffer(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.get(`/offerte/${id}`);
  }

  async createOffer(data: CreateOffertaRequest): Promise<AxiosResponse<Offerta>> {
    return this.client.post('/offerte', data);
  }

  async updateOffer(id: string, data: Partial<CreateOffertaRequest>): Promise<AxiosResponse<Offerta>> {
    return this.client.put(`/offerte/${id}`, data);
  }

  async deleteOffer(id: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`/offerte/${id}`);
  }

  async requestValidation(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/request-validation`);
  }

  async validateOffer(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/validate`);
  }

  async sendOffer(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/send`);
  }

  async acceptOffer(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/accept`);
  }

  async rejectOffer(id: string, note?: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/reject`, { note });
  }

  async downloadOfferPDF(id: string, preview: boolean = false): Promise<AxiosResponse<Blob>> {
    return this.client.get(`/offerte/${id}/pdf`, {
      params: { preview },
      responseType: 'blob',
    });
  }

  async generateOfferPDF(id: string, saveToStorage: boolean = true): Promise<AxiosResponse<{
    success: boolean;
    message: string;
    document_id?: string;
    version?: number;
    download_url?: string;
  }>> {
    return this.client.post(`/offerte/${id}/pdf/generate`, { save_to_storage: saveToStorage });
  }

  async getPDFVersions(id: string): Promise<AxiosResponse<{
    success: boolean;
    data: Array<{
      id: string;
      versione: number;
      created_at: string;
      file_size_bytes: number;
    }>;
    total: number;
  }>> {
    return this.client.get(`/offerte/${id}/pdf/versions`);
  }

  async getPDFDownloadUrl(id: string, version?: number): Promise<AxiosResponse<{
    success: boolean;
    url: string;
  }>> {
    return this.client.get(`/offerte/${id}/pdf/url`, { params: { version } });
  }

  async getPendingApproval(params?: { page?: number; limit?: number; as400_status?: string }): Promise<AxiosResponse<PaginatedResponse<Offerta>>> {
    return this.client.get('/offerte/pending-approval', { params });
  }

  async approveOfferDiscount(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/approve`);
  }

  async rejectOfferDiscount(id: string, note?: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/reject-approval`, { note });
  }

  async duplicateOffer(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/duplicate`);
  }

  async sendOfferForSignature(id: string): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/${id}/send-signature`);
  }

  async getOfferStats(params?: { from_date?: string; to_date?: string }): Promise<AxiosResponse<OffertaStats>> {
    return this.client.get('/offerte/stats', { params });
  }

  // FIC Quote Import
  async getFICQuotes(): Promise<AxiosResponse<FICQuoteListResponse>> {
    return this.client.get('/offerte/fic/quotes');
  }

  async importFICQuote(ficId: number): Promise<AxiosResponse<Offerta>> {
    return this.client.post(`/offerte/import-fic/${ficId}`);
  }

  async importAllFICQuotes(): Promise<AxiosResponse<{
    success: boolean;
    message: string;
    imported: number;
    skipped: number;
    errors: string[];
  }>> {
    return this.client.post('/offerte/import-fic-all');
  }

  // Clients endpoints moved to @offertify/api-client (endpoints/clients.ts);
  // the web app re-exports `clientsAPI` from there (see below).

  // Templates endpoints
  async getTemplates(params?: {
    page?: number;
    limit?: number;
    search?: string;
    tipo?: string;
  }): Promise<AxiosResponse<PaginatedResponse<Template>>> {
    return this.client.get('/templates', { params });
  }

  async getTemplatesForSelect(tipo?: string): Promise<AxiosResponse<Template[]>> {
    return this.client.get('/templates/select', { params: { tipo } });
  }

  async getDefaultTemplate(): Promise<AxiosResponse<Template>> {
    return this.client.get('/templates/default');
  }

  async getTemplate(id: string): Promise<AxiosResponse<Template>> {
    return this.client.get(`/templates/${id}`);
  }

  async createTemplate(data: Partial<Template>): Promise<AxiosResponse<Template>> {
    return this.client.post('/templates', data);
  }

  async updateTemplate(id: string, data: Partial<Template>): Promise<AxiosResponse<Template>> {
    return this.client.put(`/templates/${id}`, data);
  }

  async deleteTemplate(id: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`/templates/${id}`);
  }

  async renderTemplate(id: string, data: any): Promise<AxiosResponse<{ rendered_content: string }>> {
    return this.client.post(`/templates/${id}/render`, { data });
  }

  async validateTemplateSyntax(data: { contenuto: string }): Promise<AxiosResponse<{ valid: boolean; error?: string }>> {
    return this.client.post('/templates/validate', data);
  }

  // Products endpoints
  async getProducts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoria?: string;
    marca?: string;
    is_servizio?: boolean;
    is_attivo?: boolean;
  }): Promise<AxiosResponse<PaginatedResponse<Prodotto>>> {
    return this.client.get('/prodotti', { params });
  }

  async getProductsForSelect(is_attivo?: boolean): Promise<AxiosResponse<Prodotto[]>> {
    return this.client.get('/prodotti/select', { params: { is_attivo } });
  }

  async getCategories(): Promise<AxiosResponse<string[]>> {
    return this.client.get('/prodotti/categories');
  }

  async getBrands(): Promise<AxiosResponse<string[]>> {
    return this.client.get('/prodotti/marche');
  }

  async getProduct(id: string): Promise<AxiosResponse<Prodotto>> {
    return this.client.get(`/prodotti/${id}`);
  }

  async createProduct(data: Partial<Prodotto>): Promise<AxiosResponse<Prodotto>> {
    return this.client.post('/prodotti', data);
  }

  async updateProduct(id: string, data: Partial<Prodotto>): Promise<AxiosResponse<Prodotto>> {
    return this.client.put(`/prodotti/${id}`, data);
  }

  async deleteProduct(id: string): Promise<AxiosResponse<ApiResponse<void>>> {
    return this.client.delete(`/prodotti/${id}`);
  }

  async importProductsFromFIC(): Promise<AxiosResponse<{
    success: boolean;
    message: string;
    created: number;
    updated: number;
    total_from_fic: number;
  }>> {
    return this.client.post('/prodotti/import-fic');
  }

  async importMetel(file: File, mode: 'listino' | 'immagini') {
    const form = new FormData();
    form.append('file', file);
    return this.client.post(`/prodotti/metel/importa-${mode}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Product Image endpoints
  async getProductImages(prodottoId: string): Promise<AxiosResponse<ProdottoImmagine[]>> {
    return this.client.get(`/prodotti/${prodottoId}/immagini`);
  }

  async uploadProductImage(prodottoId: string, file: File, isDefault: boolean = false): Promise<AxiosResponse<ProdottoImmagine>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_default', String(isDefault));
    return this.client.post(`/prodotti/${prodottoId}/immagini`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async deleteProductImage(prodottoId: string, immagineId: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.delete(`/prodotti/${prodottoId}/immagini/${immagineId}`);
  }

  async setDefaultProductImage(prodottoId: string, immagineId: string): Promise<AxiosResponse<ProdottoImmagine>> {
    return this.client.put(`/prodotti/${prodottoId}/immagini/${immagineId}/default`);
  }

  async reorderProductImages(prodottoId: string, ordini: { id: string; ordine: number }[]): Promise<AxiosResponse<ProdottoImmagine[]>> {
    return this.client.put(`/prodotti/${prodottoId}/immagini/reorder`, { immagini: ordini });
  }

  async updateProductImage(prodottoId: string, immagineId: string, data: { alt_text?: string; ordine?: number }): Promise<AxiosResponse<ProdottoImmagine>> {
    return this.client.put(`/prodotti/${prodottoId}/immagini/${immagineId}`, data);
  }

  // Promozione Allegati endpoints
  async getPromozioneAllegati(promozioneId: string): Promise<AxiosResponse<PromozioneAllegatoResponse[]>> {
    return this.client.get(`/promozioni/${promozioneId}/allegati`);
  }

  async uploadPromozioneAllegato(
    promozioneId: string,
    file: File,
    descrizione?: string
  ): Promise<AxiosResponse<PromozioneAllegatoResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    if (descrizione) {
      formData.append('descrizione', descrizione);
    }
    return this.client.post(`/promozioni/${promozioneId}/allegati`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async deletePromozioneAllegato(promozioneId: string, allegatoId: string): Promise<AxiosResponse<void>> {
    return this.client.delete(`/promozioni/${promozioneId}/allegati/${allegatoId}`);
  }

  getPromozioneAllegatoDownloadUrl(promozioneId: string, allegatoId: string): string {
    return `${API_BASE_URL}/promozioni/${promozioneId}/allegati/${allegatoId}/download`;
  }

  // Tenant self endpoints
  async getMyTenant(): Promise<AxiosResponse<TenantMeResponse>> {
    return this.client.get('/tenants/me');
  }

  async updateMyTenant(data: TenantUpdateMeRequest): Promise<AxiosResponse<TenantMeResponse>> {
    return this.client.patch('/tenants/me', data);
  }

  // Offer numbering settings endpoints
  async getOfferNumberingSettings(): Promise<AxiosResponse<ApiResponse<OfferNumberingSettings>>> {
    return this.client.get('/offerte/numbering-settings');
  }

  async updateOfferNumberingSettings(pattern: string): Promise<AxiosResponse<ApiResponse<OfferNumberingSettings>>> {
    return this.client.put('/offerte/numbering-settings', { pattern });
  }

  // Audit logs endpoints
  async getAuditLogs(params?: AuditLogFilters): Promise<AxiosResponse<PaginatedResponse<AuditLog>>> {
    return this.client.get('/audit/logs', { params });
  }

  async getAuditLog(id: string): Promise<AxiosResponse<ApiResponse<AuditLog>>> {
    return this.client.get(`/audit/logs/${id}`);
  }

  async getAuditStats(days?: number): Promise<AxiosResponse<ApiResponse<AuditLogStats>>> {
    return this.client.get('/audit/stats', { params: { days } });
  }

  async getAuditActions(): Promise<AxiosResponse<ApiResponse<string[]>>> {
    return this.client.get('/audit/actions');
  }

  async getAuditResourceTypes(): Promise<AxiosResponse<ApiResponse<string[]>>> {
    return this.client.get('/audit/resource-types');
  }
}

export const apiClient = new APIClient();

export const authAPI = {
  getMe: () => apiClient.client.get<{ user: User; tenant: Tenant | null }>('/auth/me'),
  login: (credentials: LoginRequest) => apiClient.login(credentials),
  logout: (refresh_token?: string) => apiClient.logout(refresh_token),
  refreshToken: (refresh_token: string) => apiClient.refreshToken(refresh_token),
  // Public password recovery flow (see shared/contracts.md)
  forgotPassword: (data: ForgotPasswordRequest) =>
    apiClient.client.post<{ message: string }>('/auth/forgot-password', data),
  resetPassword: (data: ResetPasswordRequest) =>
    apiClient.client.post<{ message: string }>('/auth/reset-password', data),
  // Email change flow (see shared/contracts.md): authenticated request + public confirm.
  // 202 is intentionally non-enumerative: success, wrong password, duplicate email and
  // unsupported provider all return the same generic message.
  requestEmailChange: (data: EmailChangeRequest) =>
    apiClient.client.post<{ message: string }>('/auth/email-change/requests', data),
  confirmEmailChange: (data: EmailChangeConfirmRequest) =>
    apiClient.client.post<{ message: string }>('/auth/email-change/confirm', data),
};

export const offersAPI = {
  requestValidation: (id: string) => apiClient.requestValidation(id),
  validateOffer: (id: string) => apiClient.validateOffer(id),
  getOffers: (params?: any) => apiClient.getOffers(params),
  getOffer: (id: string) => apiClient.getOffer(id),
  createOffer: (data: CreateOffertaRequest) => apiClient.createOffer(data),
  updateOffer: (id: string, data: Partial<CreateOffertaRequest>) => apiClient.updateOffer(id, data),
  deleteOffer: (id: string) => apiClient.deleteOffer(id),
  sendOffer: (id: string) => apiClient.sendOffer(id),
  acceptOffer: (id: string) => apiClient.acceptOffer(id),
  rejectOffer: (id: string, note?: string) => apiClient.rejectOffer(id, note),
  // PDF operations
  downloadPDF: (id: string, preview?: boolean) => apiClient.downloadOfferPDF(id, preview),
  generatePDF: (id: string, saveToStorage?: boolean) => apiClient.generateOfferPDF(id, saveToStorage),
  getPDFVersions: (id: string) => apiClient.getPDFVersions(id),
  getPDFDownloadUrl: (id: string, version?: number) => apiClient.getPDFDownloadUrl(id, version),
  // Signature
  sendForSignature: (id: string) => apiClient.sendOfferForSignature(id),
  duplicateOffer: (id: string) => apiClient.duplicateOffer(id),
  getStats: (params?: { from_date?: string; to_date?: string }) => apiClient.getOfferStats(params),
  // Approval workflow
  getPendingApproval: (params?: { page?: number; limit?: number; as400_status?: string }) => apiClient.getPendingApproval(params),
  approveDiscount: (id: string) => apiClient.approveOfferDiscount(id),
  rejectDiscount: (id: string, note?: string) => apiClient.rejectOfferDiscount(id, note),
  // Discount validation preview + approvals history
  validateSconti: (data: { items: { descrizione: string; sconto_percentuale: number; prodotto_id?: string }[] }) =>
    apiClient.client.post('/offerte/validate-sconti', data),
  getDiscountApprovalsHistory: (params?: { page?: number; limit?: number }) =>
    apiClient.client.get('/offerte/discount-approvals/history', { params }),
  // Agent dashboard
  getAgentDashboard: () => apiClient.client.get('/offerte/agent/dashboard'),
  // FIC import
  getFICQuotes: () => apiClient.getFICQuotes(),
  importFICQuote: (ficId: number) => apiClient.importFICQuote(ficId),
  importAllFICQuotes: () => apiClient.importAllFICQuotes(),
  // Numbering settings
  getNumberingSettings: () => apiClient.getOfferNumberingSettings(),
  updateNumberingSettings: (pattern: string) => apiClient.updateOfferNumberingSettings(pattern),
};

export const emailTemplatesAPI = {
  getTemplates: () => apiClient.client.get('/email-templates'),
  getEvents: () => apiClient.client.get('/email-templates/events'),
  createTemplate: (data: { evento: string; oggetto: string; corpo_html: string; attivo: boolean }) =>
    apiClient.client.post('/email-templates', data),
  updateTemplate: (id: string, data: { oggetto?: string; corpo_html?: string; attivo?: boolean }) =>
    apiClient.client.put(`/email-templates/${id}`, data),
  deleteTemplate: (id: string) => apiClient.client.delete(`/email-templates/${id}`),
};

// Clienti domain now lives in @offertify/api-client. Re-exported here so the
// remaining (not-yet-migrated) consumers keep importing it from '../utils/api'.
export { clientsAPI } from '@offertify/api-client';

export const templatesAPI = {
  getTemplates: (params?: any) => apiClient.getTemplates(params),
  getTemplatesForSelect: (tipo?: string) => apiClient.getTemplatesForSelect(tipo),
  getDefaultTemplate: () => apiClient.getDefaultTemplate(),
  getTemplate: (id: string) => apiClient.getTemplate(id),
  createTemplate: (data: Partial<Template>) => apiClient.createTemplate(data),
  updateTemplate: (id: string, data: Partial<Template>) => apiClient.updateTemplate(id, data),
  deleteTemplate: (id: string) => apiClient.deleteTemplate(id),
  render: (id: string, data: any) => apiClient.renderTemplate(id, data),
  validate: (data: { contenuto: string }) => apiClient.validateTemplateSyntax(data),
};

export const productsAPI = {
  getProducts: (params?: any) => apiClient.getProducts(params),
  getProductsForSelect: (is_attivo?: boolean) => apiClient.getProductsForSelect(is_attivo),
  getCategories: () => apiClient.getCategories(),
  getBrands: () => apiClient.getBrands(),
  getProduct: (id: string) => apiClient.getProduct(id),
  createProduct: (data: Partial<Prodotto>) => apiClient.createProduct(data),
  updateProduct: (id: string, data: Partial<Prodotto>) => apiClient.updateProduct(id, data),
  deleteProduct: (id: string) => apiClient.deleteProduct(id),
  importFromFIC: () => apiClient.importProductsFromFIC(),
  importMetel: (file: File, mode: 'listino' | 'immagini') => apiClient.importMetel(file, mode),
  // Image operations
  getImmagini: (prodottoId: string) => apiClient.getProductImages(prodottoId),
  uploadImmagine: (prodottoId: string, file: File, isDefault?: boolean) => apiClient.uploadProductImage(prodottoId, file, isDefault),
  deleteImmagine: (prodottoId: string, immagineId: string) => apiClient.deleteProductImage(prodottoId, immagineId),
  setDefaultImmagine: (prodottoId: string, immagineId: string) => apiClient.setDefaultProductImage(prodottoId, immagineId),
  reorderImmagini: (prodottoId: string, ordini: { id: string; ordine: number }[]) => apiClient.reorderProductImages(prodottoId, ordini),
  updateImmagine: (prodottoId: string, immagineId: string, data: { alt_text?: string; ordine?: number }) => apiClient.updateProductImage(prodottoId, immagineId, data),
};

export const listiniAPI = {
  getListini: (params?: { page?: number; limit?: number; search?: string; is_attivo?: boolean }) =>
    apiClient.client.get<PaginatedResponse<Listino>>('/listini', { params }),
  getListiniForSelect: () =>
    apiClient.client.get<ListinoListItem[]>('/listini/select'),
  getListinoAttivo: () =>
    apiClient.client.get<Listino | null>('/listini/attivo'),
  getListino: (id: string) =>
    apiClient.client.get<Listino>(`/listini/${id}`),
  createListino: (data: Partial<Listino>) =>
    apiClient.client.post<Listino>('/listini', data),
  updateListino: (id: string, data: Partial<Listino>) =>
    apiClient.client.put<Listino>(`/listini/${id}`, data),
  deleteListino: (id: string) =>
    apiClient.client.delete(`/listini/${id}`),
  getPrezzi: (id: string, params?: { page?: number; limit?: number; search?: string; categoria?: string; marca?: string }) =>
    apiClient.client.get<PaginatedResponse<ListinoPrezzo>>(`/listini/${id}/prezzi`, { params }),
  upsertPrezzi: (id: string, prezzi: ListinoPrezzoUpsert[]) =>
    apiClient.client.put(`/listini/${id}/prezzi`, prezzi),
  bulkUpdatePrezzi: (id: string, data: BulkPriceUpdate) =>
    apiClient.client.post(`/listini/${id}/prezzi/bulk-update`, data),
  cloneListino: (id: string) =>
    apiClient.client.post<Listino>(`/listini/${id}/clone`),
  importaDaProdotti: (id: string) =>
    apiClient.client.post(`/listini/${id}/importa-da-prodotti`),
};

export const promozioniAPI = {
  getPromozioni: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    is_attiva?: boolean;
    target_tipo?: string;
    canale_id?: string[];
    settore_id?: string[];
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
  }) =>
    apiClient.client.get<PaginatedResponse<Promozione>>('/promozioni', {
      params,
      paramsSerializer: { indexes: null },
    }),
  getPromozioniAttive: () =>
    apiClient.client.get<Promozione[]>('/promozioni/attive'),
  getPromozione: (id: string) =>
    apiClient.client.get<Promozione>(`/promozioni/${id}`),
  createPromozione: (data: Partial<Promozione>) =>
    apiClient.client.post<Promozione>('/promozioni', data),
  updatePromozione: (id: string, data: Partial<Promozione>) =>
    apiClient.client.put<Promozione>(`/promozioni/${id}`, data),
  deletePromozione: (id: string) =>
    apiClient.client.delete(`/promozioni/${id}`),
  getClienti: (id: string) =>
    apiClient.client.get<{ success: boolean; data: PromozioneCliente[] }>(`/promozioni/${id}/clienti`),
  assignClienti: (id: string, clienteIds: string[]) =>
    apiClient.client.post(`/promozioni/${id}/clienti`, { cliente_ids: clienteIds }),
  removeCliente: (id: string, clienteId: string) =>
    apiClient.client.delete(`/promozioni/${id}/clienti/${clienteId}`),
  // Usage report
  getReportUtilizzo: (params?: { data_da?: string; data_a?: string; promozione_id?: string; cliente_id?: string; venditore_id?: string }) =>
    apiClient.client.get<{ success: boolean; data: PromozioneUtilizzoReportItem[] }>('/promozioni/report/utilizzo', { params }),
  // Allegati operations
  getAllegati: (promozioneId: string) => apiClient.getPromozioneAllegati(promozioneId),
  uploadAllegato: (promozioneId: string, file: File, descrizione?: string) =>
    apiClient.uploadPromozioneAllegato(promozioneId, file, descrizione),
  deleteAllegato: (promozioneId: string, allegatoId: string) =>
    apiClient.deletePromozioneAllegato(promozioneId, allegatoId),
  getAllegatoDownloadUrl: (promozioneId: string, allegatoId: string) =>
    apiClient.getPromozioneAllegatoDownloadUrl(promozioneId, allegatoId),
};

export const pricingAPI = {
  resolvePrice: (prodotto_id: string, cliente_id: string) =>
    apiClient.client.post<PriceResolution>('/pricing/resolve', { prodotto_id, cliente_id }),
  resolvePricesBatch: (cliente_id: string, prodotto_ids: string[]) =>
    apiClient.client.post<{ prezzi: PriceResolution[] }>('/pricing/resolve-batch', { cliente_id, prodotto_ids }),
};

export const autorizzazioniScontoAPI = {
  getAutorizzazioni: (params?: { page?: number; limit?: number; user_id?: string }) =>
    apiClient.client.get<PaginatedResponse<AutorizzazioneSconto>>('/autorizzazioni-sconto', { params }),
  getByUser: (userId: string) =>
    apiClient.client.get<{ success: boolean; data: AutorizzazioneSconto[] }>(`/autorizzazioni-sconto/user/${userId}`),
  createAutorizzazione: (data: { user_id: string; scope_tipo: string; scope_categoria?: string; sconto_max: number }) =>
    apiClient.client.post<AutorizzazioneSconto>('/autorizzazioni-sconto', data),
  updateAutorizzazione: (id: string, data: { scope_tipo?: string; scope_categoria?: string; sconto_max?: number }) =>
    apiClient.client.put<AutorizzazioneSconto>(`/autorizzazioni-sconto/${id}`, data),
  deleteAutorizzazione: (id: string) =>
    apiClient.client.delete(`/autorizzazioni-sconto/${id}`),
};

export const auditAPI = {
  getLogs: (params?: AuditLogFilters) => apiClient.getAuditLogs(params),
  getLog: (id: string) => apiClient.getAuditLog(id),
  getStats: (days?: number) => apiClient.getAuditStats(days),
  getActions: () => apiClient.getAuditActions(),
  getResourceTypes: () => apiClient.getAuditResourceTypes(),
};

export const tenantsAPI = {
  getTenants: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    piano?: string;
    is_active?: boolean;
  }) => apiClient.client.get('/tenants', { params }),
  getTenant: (id: string) => apiClient.client.get(`/tenants/${id}`),
  createTenant: (data: {
    nome: string;
    slug: string;
    email_admin: string;
    piano_id: string;
    partita_iva?: string;
    codice_fiscale?: string;
    indirizzo?: string;
    telefono?: string;
    sito_web?: string;
  }) => apiClient.client.post('/tenants', data),
  updateTenant: (id: string, data: any) => apiClient.client.put(`/tenants/${id}`, data),
  deleteTenant: (id: string) => apiClient.client.delete(`/tenants/${id}`),
  impersonate: (tenant_id: string, user_id?: string) => apiClient.client.post('/auth/impersonate', { tenant_id, user_id }),
  stopImpersonation: () => apiClient.client.post('/auth/stop-impersonation'),
  getMe: () => apiClient.getMyTenant(),
  updateMe: (data: TenantUpdateMeRequest) => apiClient.updateMyTenant(data),
};

export const pianiAPI = {
  getPiani: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
    is_public?: boolean;
  }) => apiClient.client.get('/piani', { params }),
  getPiano: (id: string) => apiClient.client.get(`/piani/${id}`),
  createPiano: (data: any) => apiClient.client.post('/piani', data),
  updatePiano: (id: string, data: any) => apiClient.client.put(`/piani/${id}`, data),
  deletePiano: (id: string) => apiClient.client.delete(`/piani/${id}`),
};

export const ficAPI = {
  getStatus: () => apiClient.client.get('/fic/status'),
  saveCredentials: (data: { client_id: string; client_secret: string }) =>
    apiClient.client.post('/fic/credentials', data),
  deleteCredentials: () => apiClient.client.delete('/fic/credentials'),
  initOAuth: () => apiClient.client.get('/fic/auth/init'),
  handleCallback: (data: { code: string; state: string }) =>
    apiClient.client.post('/fic/auth/callback', data),
  disconnect: () => apiClient.client.post('/fic/disconnect'),
  getClients: (params?: { page?: number; per_page?: number }) =>
    apiClient.client.get('/fic/clients', { params }),
  getProducts: (params?: { page?: number; per_page?: number }) =>
    apiClient.client.get('/fic/products', { params }),
  getQuotes: (params?: { page?: number; per_page?: number }) =>
    apiClient.client.get('/fic/quotes', { params }),
};

// Visite domain now lives in @offertify/api-client. Re-exported here so the
// remaining (not-yet-migrated) consumers keep importing it from '../utils/api'.
export { visiteAPI } from '@offertify/api-client';

// AI API endpoints
export const aiAPI = {
  // AI Status and Config
  getStatus: () => apiClient.client.get('/ai/status'),
  getConfig: () => apiClient.client.get('/ai/config'),
  createConfig: (data: any) => apiClient.client.post('/ai/config', data),
  updateConfig: (data: any) => apiClient.client.patch('/ai/config', data),
  getUsageStats: () => apiClient.client.get('/ai/usage/stats'),
  getUsageStatsForTenant: (tenantId: string) => apiClient.client.get(`/ai/usage/stats/${tenantId}`),
  getUsageHistory: (months: number = 6) =>
    apiClient.client.get('/ai/usage/history', { params: { months } }),
  getUsageHistoryForTenant: (tenantId: string, months: number = 6) =>
    apiClient.client.get(`/ai/usage/history/${tenantId}`, { params: { months } }),

  // Host-only tenant AI config
  getConfigForTenant: (tenantId: string) => apiClient.client.get(`/ai/config/${tenantId}`),
  createConfigForTenant: (tenantId: string, data: any) => apiClient.client.post(`/ai/config/${tenantId}`, data),
  updateConfigForTenant: (tenantId: string, data: any) => apiClient.client.patch(`/ai/config/${tenantId}`, data),

  // AI Operations
  suggest: (data: {
    current_text: string;
    context?: string;
    field_name?: string;
    offerta_id?: string;
  }) => apiClient.client.post('/ai/suggest', data),

  generate: (data: {
    section_type: string;
    context: Record<string, unknown>;
    custom_instructions?: string;
    offerta_id?: string;
  }) => apiClient.client.post('/ai/generate', data),

  improve: (data: {
    text: string;
    improvement_type: string;
    context?: string;
    offerta_id?: string;
  }) => apiClient.client.post('/ai/improve', data),

  expand: (data: {
    text: string;
    target_length: string;
    context?: string;
    offerta_id?: string;
  }) => apiClient.client.post('/ai/expand', data),

  custom: (data: {
    system_prompt?: string;
    user_prompt: string;
    temperature?: number;
    max_tokens?: number;
    offerta_id?: string;
  }) => apiClient.client.post('/ai/custom', data),

  // Streaming generation (SSE)
  generateStream: async function* (data: {
    section_type: string;
    context: Record<string, unknown>;
    custom_instructions?: string;
    offerta_id?: string;
  }) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/ai/generate/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield data;
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }
  },
};

// Messages API endpoints
export const messagesAPI = {
  getMessages: (params?: {
    page?: number;
    limit?: number;
    channel?: string;
    direction?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }) => apiClient.client.get('/messages', { params }),
  getMessage: (id: string) => apiClient.client.get(`/messages/${id}`),
  getStats: (params?: { days?: number }) => apiClient.client.get('/messages/stats', { params }),
};

export const conversationsAPI = {
  getConversations: (params?: {
    page?: number;
    limit?: number;
    channel?: string;
    search?: string;
  }) => apiClient.client.get('/conversations', { params }),
  getConversationMessages: (contactId: string, params: {
    channel: string;
    page?: number;
    limit?: number;
  }) => apiClient.client.get(`/conversations/${encodeURIComponent(contactId)}/messages`, { params }),
};

// Webhook Events API endpoints
export const webhookEventsAPI = {
  getEvents: (params?: {
    page?: number;
    limit?: number;
    channel?: string;
    processing_status?: string;
    date_from?: string;
    date_to?: string;
  }) => apiClient.client.get('/webhook-events', { params }),
  getEvent: (id: string) => apiClient.client.get(`/webhook-events/${id}`),
};

// Users API endpoints
export const usersAPI = {
  // Get all users (with pagination and filters)
  getUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: UserRole;
    is_active?: boolean;
    tenant_id?: string;
  }): Promise<AxiosResponse<PaginatedResponse<User>>> =>
    apiClient.client.get('/users', { params }),

  // Get host users only
  getHostUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
  }): Promise<AxiosResponse<PaginatedResponse<User>>> =>
    apiClient.client.get('/users/host', { params }),

  // Get single user
  getUser: (id: string): Promise<AxiosResponse<{ success: boolean; data: User }>> =>
    apiClient.client.get(`/users/${id}`),

  // Create user (in current tenant)
  createUser: (data: UserCreateRequest): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.post('/users', data),

  // Create host user (tenant_id = null)
  createHostUser: (data: UserCreateRequest & { tenant_id?: null }): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.post('/users/host', data),

  // Update user
  updateUser: (id: string, data: UserUpdateRequest): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.put(`/users/${id}`, data),

  // Change own password
  changePassword: (id: string, data: PasswordChangeRequest): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    apiClient.client.put(`/users/${id}/password`, data),

  // Admin reset password
  resetPassword: (id: string, data: PasswordResetRequest): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    apiClient.client.put(`/users/${id}/reset-password`, data),

  // Delete user (soft delete)
  deleteUser: (id: string): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    apiClient.client.delete(`/users/${id}`),

  // Activate user
  activateUser: (id: string): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.post(`/users/${id}/activate`),

  // Deactivate user
  deactivateUser: (id: string): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.post(`/users/${id}/deactivate`),

  // Update own profile (no special permissions needed)
  updateMyProfile: (data: { nome?: string; cognome?: string; telefono?: string }): Promise<AxiosResponse<{ success: boolean; data: User; message?: string }>> =>
    apiClient.client.put('/users/me/profile', data),

  // Get roles and permissions info
  getRolesPermissions: (): Promise<AxiosResponse<RolesPermissionsResponse>> =>
    apiClient.client.get('/users/roles-permissions'),

  // Cono di visibilità (issue #54): utenti del tenant per il picker "Responsabile"
  // e i filtri agente — scoped al proprio cono se l'utente non ha users:view.
  getHierarchy: (): Promise<AxiosResponse<UserBrief[]>> =>
    apiClient.client.get('/users/hierarchy'),
};

// Geocoding API
export const geocodingAPI = {
  getSettings: () => apiClient.client.get('/geocoding/settings'),
  updateSettings: (data: {
    provider: 'nominatim' | 'google';
    nominatim_url?: string;
    google_api_key?: string;
  }) => apiClient.client.put('/geocoding/settings', data),
  geocode: (data: {
    indirizzo?: string;
    citta?: string;
    provincia?: string;
    cap?: string;
  }) => apiClient.client.post('/geocoding/geocode', data),
};

// Portale Cliente API
export const portaleAPI = {
  getCatalog: (params?: { page?: number; limit?: number; search?: string; categoria?: string; marca?: string }) =>
    apiClient.client.get<{ success: boolean; data: CatalogProduct[]; pagination: { page: number; limit: number; total: number; pages: number } }>('/portale/catalogo', { params }),
  getCategorie: () =>
    apiClient.client.get<string[]>('/portale/catalogo/categorie'),
  getMarche: () =>
    apiClient.client.get<string[]>('/portale/catalogo/marche'),
  getMyOrders: (params?: { page?: number; limit?: number; stato?: string }) =>
    apiClient.client.get<PaginatedResponse<Offerta>>('/portale/ordini', { params }),
  getOrder: (id: string) =>
    apiClient.client.get<Offerta>(`/portale/ordini/${id}`),
  createOrder: (data: CustomerOrderCreate) =>
    apiClient.client.post<Offerta>('/portale/ordini', data),
  updateOrder: (id: string, data: CustomerOrderUpdate) =>
    apiClient.client.put<Offerta>(`/portale/ordini/${id}`, data),
  deleteOrder: (id: string) =>
    apiClient.client.delete(`/portale/ordini/${id}`),
  getProfile: () =>
    apiClient.client.get<CustomerProfile>('/portale/profilo'),
  updateProfile: (data: Partial<CustomerProfile>) =>
    apiClient.client.put<CustomerProfile>('/portale/profilo', data),
};

// Roles API
export const rolesAPI = {
  // Get all roles with their permissions
  getAllRoles: (): Promise<AxiosResponse<Record<string, any>>> =>
    apiClient.client.get('/roles'),

  // Get specific role permissions
  getRolePermissions: (role: string): Promise<AxiosResponse<any>> =>
    apiClient.client.get(`/roles/${role}`),

  // Update role permissions
  updateRolePermissions: (
    role: string,
    data: { permissions: string[]; description?: string }
  ): Promise<AxiosResponse<any>> =>
    apiClient.client.put(`/roles/${role}`, data),
};

// Platform Settings API (host-only)
export const platformSettingsAPI = {
  getTelegramSettings: () =>
    apiClient.client.get<{ success: boolean; data: {
      telegram_bot_token_set: boolean;
      telegram_session_expire_hours: number;
      telegram_max_pin_attempts: number;
      telegram_pin_lockout_minutes: number;
      source: string;
    } }>('/platform-settings/telegram'),
  updateTelegramSettings: (data: {
    telegram_bot_token?: string;
    telegram_session_expire_hours?: number;
    telegram_max_pin_attempts?: number;
    telegram_pin_lockout_minutes?: number;
  }) =>
    apiClient.client.put('/platform-settings/telegram', data),
  restartTelegramBot: () =>
    apiClient.client.post<{ success: boolean; status: string; bot_username: string | null }>('/telegram/restart'),
};

export const syncAdminAPI = {
  listTenants: (params?: { heartbeat_window_seconds?: number }) =>
    apiClient.client.get<{ success: boolean; data: SyncTenantAccess[] }>('/sync-admin/tenants', { params }),
  updateTenantAccess: (tenantId: string, syncEnabled: boolean) =>
    apiClient.client.put<SyncTenantAccess>(`/sync-admin/tenants/${tenantId}/access`, { sync_enabled: syncEnabled }),
  createDaemonToken: (tenantId: string, data: { node_id: string }) =>
    apiClient.client.post<{
      tenant_id: string;
      node_id: string;
      token: string;
      token_hint: string;
      expires_at: string | null;
    }>(`/sync-admin/tenants/${tenantId}/nodes/token`, data),
  listDaemonNodes: (tenantId: string, params?: { heartbeat_window_seconds?: number }) =>
    apiClient.client.get<{ success: boolean; data: SyncDaemonNode[] }>(`/sync-admin/tenants/${tenantId}/nodes`, { params }),
  revokeDaemonNode: (tenantId: string, nodeId: string) =>
    apiClient.client.post<{ success: boolean; message: string }>(
      `/sync-admin/tenants/${tenantId}/nodes/${encodeURIComponent(nodeId)}/revoke`
    ),
  listQueries: (tenantId: string) =>
    apiClient.client.get<{ success: boolean; data: SyncQueryDefinition[] }>(`/sync-admin/tenants/${tenantId}/queries`),
  createQuery: (tenantId: string, data: {
    query_id: string;
    source_type?: 'as400' | 'google_drive' | 'as400_export';
    source_config?: Record<string, unknown>;
    schedule_window?: { start?: string; end?: string; timezone?: string; weekdays?: number[] };
    name: string;
    description?: string;
    sql_text: string;
    params_schema?: Record<string, any>;
    business_key_fields: string[];
    source_ts_field?: string;
    schedule_minutes?: number;
    is_active?: boolean;
  }) => apiClient.client.post<SyncQueryDefinition>(`/sync-admin/tenants/${tenantId}/queries`, data),
  updateQuery: (tenantId: string, queryId: string, data: Partial<{
    source_type?: 'as400' | 'google_drive' | 'as400_export';
    source_config?: Record<string, unknown>;
    schedule_window?: { start?: string; end?: string; timezone?: string; weekdays?: number[] };
    name: string;
    description: string;
    sql_text: string;
    params_schema: Record<string, any>;
    business_key_fields: string[];
    source_ts_field: string;
    schedule_minutes: number;
    is_active: boolean;
  }>) => apiClient.client.put<SyncQueryDefinition>(
    `/sync-admin/tenants/${tenantId}/queries/${encodeURIComponent(queryId)}`,
    data
  ),
  disableQuery: (tenantId: string, queryId: string) =>
    apiClient.client.delete<{ success: boolean; message: string }>(
      `/sync-admin/tenants/${tenantId}/queries/${encodeURIComponent(queryId)}`
    ),
  dispatchJob: (tenantId: string, data: {
    node_id: string;
    query_id?: string;
    command_type?: 'sync' | 'query_preview' | 'list_tables' | 'list_columns';
    params?: Record<string, any>;
    sql_text?: string;
    schema_name?: string;
    table_name?: string;
    page?: number;
    page_size?: number;
    max_pages?: number;
    priority?: number;
  }) =>
    apiClient.client.post<SyncDispatchJob & { success: boolean; mqtt_topic: string }>(
      `/sync-admin/tenants/${tenantId}/jobs/dispatch`,
      data
    ),
  listJobs: (tenantId: string, params?: { limit?: number }) =>
    apiClient.client.get<{ success: boolean; data: SyncDispatchJob[] }>(`/sync-admin/tenants/${tenantId}/jobs`, { params }),
  getJob: (tenantId: string, jobId: string) =>
    apiClient.client.get<SyncDispatchJob>(`/sync-admin/tenants/${tenantId}/jobs/${encodeURIComponent(jobId)}`),
  resetCdc: (tenantId: string, queryId: string) =>
    apiClient.client.post<{ success: boolean; dispatched: number; job_ids: string[] }>(
      `/sync-admin/tenants/${tenantId}/queries/${encodeURIComponent(queryId)}/reset-cdc`
    ),
  resetCdcAll: (tenantId: string) =>
    apiClient.client.post<{ success: boolean; dispatched: number; job_ids: string[] }>(
      `/sync-admin/tenants/${tenantId}/queries/reset-cdc-all`
    ),
};

// WhatsApp Config API (tenant-scoped)
export const whatsappAPI = {
  getConfig: () =>
    apiClient.client.get<{ success: boolean; data: {
      phone_number_id: string | null;
      verify_token: string | null;
      business_id: string | null;
      display_name: string | null;
      phone_number: string | null;
      is_active: boolean;
      webhook_verified_at: string | null;
      whatsapp_token_set: boolean;
      app_secret_set: boolean;
    } | null }>('/whatsapp/config'),
  updateConfig: (data: {
    whatsapp_token?: string;
    phone_number_id?: string;
    verify_token?: string;
    app_secret?: string;
    business_id?: string;
    display_name?: string;
    phone_number?: string;
    is_active?: boolean;
  }) =>
    apiClient.client.put('/whatsapp/config', data),
  deleteConfig: () =>
    apiClient.client.delete('/whatsapp/config'),
  getWebhookUrl: () =>
    apiClient.client.get<{ success: boolean; data: {
      webhook_url: string;
      verify_token: string | null;
    } }>('/whatsapp/config/webhook-url'),
};

// Storage (S3 per-tenant) API
export const storageConfigAPI = {
  getStatus: () =>
    apiClient.client.get<StorageConfigStatus>('/storage/config'),
  saveConfig: (data: {
    provider: StorageProvider;
    endpoint: string;
    public_endpoint?: string;
    bucket: string;
    region?: string;
    secure?: boolean;
    access_key: string;
    secret_key: string;
  }) =>
    apiClient.client.post('/storage/config', data),
  updateConfig: (data: {
    provider?: StorageProvider;
    endpoint?: string;
    public_endpoint?: string;
    bucket?: string;
    region?: string;
    secure?: boolean;
    access_key?: string;
    secret_key?: string;
  }) =>
    apiClient.client.put('/storage/config', data),
  testConnection: () =>
    apiClient.client.post<{ success: boolean; message: string }>('/storage/test-connection'),
  disconnect: () =>
    apiClient.client.post('/storage/disconnect'),
};

// WMS Integration API
export const wmsAPI = {
  // Config
  getStatus: () =>
    apiClient.client.get<WmsConnectionStatus>('/wms/status'),
  saveConfig: (data: {
    base_url: string;
    api_token?: string;
  auth_mode?: 'api_token' | 'client_credentials';
  oauth_authority?: string;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_tenant_id?: string;
    default_warehouse_id?: string;
    default_warehouse_name?: string;
    auto_create_order?: boolean;
    order_requires_approval?: boolean;
  }) =>
    apiClient.client.post('/wms/config', data),
  updateConfig: (data: {
    base_url?: string;
    api_token?: string;
  auth_mode?: 'api_token' | 'client_credentials';
  oauth_authority?: string;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  oauth_tenant_id?: string;
    default_warehouse_id?: string;
    default_warehouse_name?: string;
    auto_create_order?: boolean;
    order_requires_approval?: boolean;
  }) =>
    apiClient.client.put('/wms/config', data),
  testConnection: () =>
    apiClient.client.post<{ success: boolean; message: string }>('/wms/test-connection'),
  disconnect: () =>
    apiClient.client.post('/wms/disconnect'),
  getWarehouses: () =>
    apiClient.client.get<{ data: Array<{ id: string; name: string }> }>('/wms/warehouses'),

  // Mapping
  getMappings: (params?: { page?: number; limit?: number }) =>
    apiClient.client.get<PaginatedResponse<WmsMapping>>('/wms/mappings', { params }),
  createMapping: (data: {
    prodotto_id: string;
    wms_material_id: string;
    wms_material_code: string;
    wms_material_name?: string;
  }) =>
    apiClient.client.post<WmsMapping>('/wms/mappings', data),
  deleteMapping: (id: string) =>
    apiClient.client.delete(`/wms/mappings/${id}`),
  autoMatch: () =>
    apiClient.client.post<WmsAutoMatchResult>('/wms/auto-match'),
  searchMaterials: (search: string) =>
    apiClient.client.get<{ data: Array<{ id: string; code: string; name?: string }> }>('/wms/materials', { params: { search } }),

  // Stock
  getStock: (prodottoId: string) =>
    apiClient.client.get<WmsStockInfo>(`/wms/stock/${prodottoId}`),
  getStockBatch: (prodottoIds: string[]) =>
    apiClient.client.post<{ items: WmsStockInfo[] }>('/wms/stock/batch', { prodotto_ids: prodottoIds }),
  getStockSummary: () =>
    apiClient.client.get<{ total_mapped: number; total_unmapped: number; in_stock: number; low_stock: number; out_of_stock: number }>('/wms/stock/summary'),

  // Orders
  createOrder: (offertaId: string) =>
    apiClient.client.post<{ success: boolean; wms_order_id: string; wms_order_number: string; wms_order_status: string; stato_evasione: string }>(`/wms/orders/create/${offertaId}`),
  confirmOrder: (offertaId: string) =>
    apiClient.client.post<{ success: boolean; wms_order_status: string; stato_evasione: string }>(`/wms/orders/confirm/${offertaId}`),
  getOrderStatus: (offertaId: string) =>
    apiClient.client.get<WmsOrderStatus>(`/wms/orders/status/${offertaId}`),
  syncOrderStatus: (offertaId: string) =>
    apiClient.client.post<{ success: boolean; wms_order_status: string; stato_evasione: string }>(`/wms/orders/sync-status/${offertaId}`),
};

// Leads API (Lead Core)
function buildLeadParams(filters?: LeadFilters): Record<string, unknown> | undefined {
  if (!filters) return undefined;
  const params: Record<string, unknown> = {};
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.stato && filters.stato.length) params.stato = filters.stato;
  if (filters.source && filters.source.length) params.source = filters.source;
  if (filters.agente_id) params.agente_id = filters.agente_id;
  if (filters.search) params.search = filters.search;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.tag_ids && filters.tag_ids.length) params.tag_id = filters.tag_ids;
  return params;
}

export const leadsAPI = {
  getLeads: (filters?: LeadFilters): Promise<AxiosResponse<LeadListResponse>> =>
    apiClient.client.get('/leads', {
      params: buildLeadParams(filters),
      paramsSerializer: { indexes: null },
    }),

  getLeadsForSelect: (): Promise<AxiosResponse<LeadBrief[]>> =>
    apiClient.client.get('/leads/select'),

  getLead: (id: string): Promise<AxiosResponse<LeadResponse>> =>
    apiClient.client.get(`/leads/${id}`),

  createLead: (data: LeadCreate): Promise<AxiosResponse<LeadResponse>> =>
    apiClient.client.post('/leads', data),

  updateLead: (id: string, data: LeadUpdate): Promise<AxiosResponse<LeadResponse>> =>
    apiClient.client.put(`/leads/${id}`, data),

  deleteLead: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/${id}`),

  listActivities: (
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<AxiosResponse<LeadActivityListResponse>> =>
    apiClient.client.get(`/leads/${id}/activities`, { params }),

  createActivity: (
    id: string,
    data: LeadActivityCreate
  ): Promise<AxiosResponse<LeadActivityResponse>> =>
    apiClient.client.post(`/leads/${id}/activities`, data),

  assignLead: (
    id: string,
    data: LeadAssignRequest
  ): Promise<AxiosResponse<LeadResponse>> =>
    apiClient.client.post(`/leads/${id}/assign`, data),

  convertLead: (
    id: string,
    data: LeadConvertRequest
  ): Promise<AxiosResponse<LeadConvertResponse>> =>
    apiClient.client.post(`/leads/${id}/convert`, data),

  conversionPreview: (id: string): Promise<AxiosResponse<LeadConvertPreview>> =>
    apiClient.client.get(`/leads/${id}/conversion-preview`),

  getPipeline: (filters?: LeadFilters): Promise<AxiosResponse<LeadPipelineResponse>> =>
    apiClient.client.get('/leads/pipeline', {
      params: buildLeadParams(filters),
      paramsSerializer: { indexes: null },
    }),

  getStats: (): Promise<AxiosResponse<LeadStats>> =>
    apiClient.client.get('/leads/stats'),
};

// Lead Intake Forms API (admin CRUD)
export const leadIntakeFormsAPI = {
  getForms: (params?: {
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<LeadIntakeFormListResponse>> =>
    apiClient.client.get('/leads/intake-forms', { params }),

  getForm: (id: string): Promise<AxiosResponse<LeadIntakeFormResponse>> =>
    apiClient.client.get(`/leads/intake-forms/${id}`),

  createForm: (
    data: LeadIntakeFormCreate
  ): Promise<AxiosResponse<LeadIntakeFormResponse>> =>
    apiClient.client.post('/leads/intake-forms', data),

  updateForm: (
    id: string,
    data: LeadIntakeFormUpdate
  ): Promise<AxiosResponse<LeadIntakeFormResponse>> =>
    apiClient.client.put(`/leads/intake-forms/${id}`, data),

  deleteForm: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/intake-forms/${id}`),

  rotateSlug: (id: string): Promise<AxiosResponse<RotateSlugResponse>> =>
    apiClient.client.post(`/leads/intake-forms/${id}/rotate-slug`),

  getStats: (id: string): Promise<AxiosResponse<LeadIntakeFormStats>> =>
    apiClient.client.get(`/leads/intake-forms/${id}/stats`),
};

// Lead Tags API
export const leadTagsAPI = {
  getTags: (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<AxiosResponse<LeadTagListResponse>> =>
    apiClient.client.get('/leads/tags', { params }),

  getTag: (id: string): Promise<AxiosResponse<LeadTagResponse>> =>
    apiClient.client.get(`/leads/tags/${id}`),

  createTag: (data: LeadTagCreate): Promise<AxiosResponse<LeadTagResponse>> =>
    apiClient.client.post('/leads/tags', data),

  updateTag: (id: string, data: LeadTagUpdate): Promise<AxiosResponse<LeadTagResponse>> =>
    apiClient.client.put(`/leads/tags/${id}`, data),

  deleteTag: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/tags/${id}`),

  // Attach/detach/list-per-lead (mounted under /leads/{lead_id}/tags)
  getLeadTags: (leadId: string): Promise<AxiosResponse<LeadTagResponse[]>> =>
    apiClient.client.get(`/leads/${leadId}/tags`),

  attachToLead: (
    leadId: string,
    tagId: string
  ): Promise<AxiosResponse<LeadTagAttachResponse>> =>
    apiClient.client.post(`/leads/${leadId}/tags`, { tag_id: tagId }),

  detachFromLead: (leadId: string, tagId: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/${leadId}/tags/${tagId}`),
};

// Activity Types API (anagrafica tipologie attività lead/cliente/offerta)
export const activityTypesAPI = {
  getTypes: (params?: {
    context?: ActivityTypeContext;
    active_only?: boolean;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<AxiosResponse<ActivityTypeListResponse>> =>
    apiClient.client.get('/activity-types', { params }),

  getType: (id: string): Promise<AxiosResponse<ActivityTypeResponse>> =>
    apiClient.client.get(`/activity-types/${id}`),

  createType: (data: ActivityTypeCreate): Promise<AxiosResponse<ActivityTypeResponse>> =>
    apiClient.client.post('/activity-types', data),

  updateType: (id: string, data: ActivityTypeUpdate): Promise<AxiosResponse<ActivityTypeResponse>> =>
    apiClient.client.put(`/activity-types/${id}`, data),

  deleteType: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/activity-types/${id}`),
};

// Lead Scoring Rules API
export const leadScoringRulesAPI = {
  getRules: (params?: {
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<LeadScoreRuleListResponse>> =>
    apiClient.client.get('/leads/scoring-rules', { params }),

  getRule: (id: string): Promise<AxiosResponse<LeadScoreRuleResponse>> =>
    apiClient.client.get(`/leads/scoring-rules/${id}`),

  createRule: (
    data: LeadScoreRuleCreate
  ): Promise<AxiosResponse<LeadScoreRuleResponse>> =>
    apiClient.client.post('/leads/scoring-rules', data),

  updateRule: (
    id: string,
    data: LeadScoreRuleUpdate
  ): Promise<AxiosResponse<LeadScoreRuleResponse>> =>
    apiClient.client.put(`/leads/scoring-rules/${id}`, data),

  deleteRule: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/scoring-rules/${id}`),

  toggleRule: (id: string): Promise<AxiosResponse<ToggleResponse>> =>
    apiClient.client.post(`/leads/scoring-rules/${id}/toggle`),

  recomputeAll: (): Promise<AxiosResponse<RecomputeAllResponse>> =>
    apiClient.client.post('/leads/scoring-rules/recompute-all'),

  recomputeOne: (leadId: string): Promise<AxiosResponse<ScoreChangeResponse>> =>
    apiClient.client.post(`/leads/${leadId}/recompute-score`),
};

// Lead Segments API (Phase 5 consumer)
export const leadSegmentsAPI = {
  getSegments: (params?: {
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<LeadSegmentListResponse>> =>
    apiClient.client.get('/leads/segments', { params }),

  getSegment: (id: string): Promise<AxiosResponse<LeadSegmentResponse>> =>
    apiClient.client.get(`/leads/segments/${id}`),

  createSegment: (
    data: LeadSegmentCreate
  ): Promise<AxiosResponse<LeadSegmentResponse>> =>
    apiClient.client.post('/leads/segments', data),

  updateSegment: (
    id: string,
    data: LeadSegmentUpdate
  ): Promise<AxiosResponse<LeadSegmentResponse>> =>
    apiClient.client.put(`/leads/segments/${id}`, data),

  deleteSegment: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/segments/${id}`),

  preview: (id: string): Promise<AxiosResponse<LeadSegmentPreview>> =>
    apiClient.client.get(`/leads/segments/${id}/preview`),

  // Export CSV: returns Blob so caller can create download link
  exportCSV: (params?: Record<string, unknown>): Promise<AxiosResponse<Blob>> =>
    apiClient.client.get('/leads/export', {
      params,
      responseType: 'blob',
      paramsSerializer: { indexes: null },
    }),
};

// Lead Campaigns API
export const leadCampaignsAPI = {
  getCampaigns: (params?: {
    page?: number;
    limit?: number;
    state?: CampaignState;
  }): Promise<AxiosResponse<LeadCampaignListResponse>> =>
    apiClient.client.get('/leads/campaigns', { params }),

  getCampaign: (id: string): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.get(`/leads/campaigns/${id}`),

  createCampaign: (
    data: LeadCampaignCreate
  ): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.post('/leads/campaigns', data),

  updateCampaign: (
    id: string,
    data: LeadCampaignUpdate
  ): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.put(`/leads/campaigns/${id}`, data),

  deleteCampaign: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/campaigns/${id}`),

  start: (id: string): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.post(`/leads/campaigns/${id}/start`),

  pause: (id: string): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.post(`/leads/campaigns/${id}/pause`),

  resume: (id: string): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.post(`/leads/campaigns/${id}/resume`),

  archive: (id: string): Promise<AxiosResponse<LeadCampaignResponse>> =>
    apiClient.client.post(`/leads/campaigns/${id}/archive`),

  getMetrics: (id: string): Promise<AxiosResponse<LeadCampaignMetrics>> =>
    apiClient.client.get(`/leads/campaigns/${id}/metrics`),

  getDeliveries: (
    id: string,
    params?: { page?: number; limit?: number; state?: DeliveryFilterState }
  ): Promise<AxiosResponse<LeadCampaignDeliveryListResponse>> =>
    apiClient.client.get(`/leads/campaigns/${id}/deliveries`, { params }),

  getSteps: (id: string): Promise<AxiosResponse<LeadCampaignStepResponse[]>> =>
    apiClient.client.get(`/leads/campaigns/${id}/steps`),

  createStep: (
    id: string,
    data: LeadCampaignStepCreate
  ): Promise<AxiosResponse<LeadCampaignStepResponse>> =>
    apiClient.client.post(`/leads/campaigns/${id}/steps`, data),

  updateStep: (
    id: string,
    stepId: string,
    data: LeadCampaignStepUpdate
  ): Promise<AxiosResponse<LeadCampaignStepResponse>> =>
    apiClient.client.put(`/leads/campaigns/${id}/steps/${stepId}`, data),

  deleteStep: (id: string, stepId: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/leads/campaigns/${id}/steps/${stepId}`),

  previewStep: (
    id: string,
    stepId: string,
    leadId: string
  ): Promise<AxiosResponse<PreviewStepResponse>> =>
    apiClient.client.post(
      `/leads/campaigns/${id}/preview-step/${stepId}`,
      undefined,
      { params: { lead_id: leadId } }
    ),
};

// Chat (In-App AI Assistant) API
export const chatAPI = {
  getSessions: (params?: { page?: number; limit?: number }) =>
    apiClient.client.get<{ sessions: ChatSession[]; total: number; page: number; limit: number }>('/chat/sessions', { params }),

  getSession: (id: string) =>
    apiClient.client.get<ChatSessionDetail>(`/chat/sessions/${id}`),

  updateSession: (id: string, data: { title: string }) =>
    apiClient.client.patch<ChatSession>(`/chat/sessions/${id}`, data),

  deleteSession: (id: string) =>
    apiClient.client.delete(`/chat/sessions/${id}`),

  confirmAction: (sessionId: string, confirmed: boolean) =>
    apiClient.client.post(`/chat/sessions/${sessionId}/confirm`, { confirmed }),

  sendMessage: async function* (sessionId: string | null, message: string, imageBase64?: string) {
    const token = localStorage.getItem('access_token');
    const body: Record<string, unknown> = {
      session_id: sessionId,
      message,
    };
    if (imageBase64) body.image_base64 = imageBase64;

    const response = await fetch(`${API_BASE_URL}/chat/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield data;
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }
  },
};

// OpenAPI.com Integration — endpoint tenant
export const openapiAPI = {
  validatePi: (
    data: OpenapiValidatePiRequest
  ): Promise<AxiosResponse<OpenapiValidatePiResponse>> =>
    apiClient.client.post('/openapi/validate-pi', data),

  searchAdvanced: (
    data: OpenapiSearchRequest
  ): Promise<AxiosResponse<OpenapiSearchResponse>> =>
    apiClient.client.post('/openapi/search', data),

  requestVisura: (
    data: OpenapiVisuraRequest
  ): Promise<AxiosResponse<OpenapiVisuraTicket>> =>
    apiClient.client.post('/openapi/visura', data),

  pollVisura: (
    externalId: string
  ): Promise<AxiosResponse<OpenapiVisuraStatusResponse>> =>
    apiClient.client.get(`/openapi/visura/${externalId}`),

  getQuota: (): Promise<AxiosResponse<OpenapiQuotaResponse>> =>
    apiClient.client.get('/openapi/quota'),

  requestUpgrade: (
    data: OpenapiUpgradeRequestCreate
  ): Promise<AxiosResponse<OpenapiUpgradeRequestResponse>> =>
    apiClient.client.post('/openapi/upgrade-requests', data),

  listUpgradeRequests: (params?: {
    stato?: OpenapiUpgradeRequestState;
    page?: number;
    size?: number;
  }): Promise<AxiosResponse<OpenapiUpgradeRequestListResponse>> =>
    apiClient.client.get('/openapi/upgrade-requests/me', { params }),
};

// OpenAPI.com Integration — endpoint host (super-admin)
export const hostOpenapiAPI = {
  listUpgradeRequests: (params?: {
    stato?: OpenapiUpgradeRequestState;
    tenant_id?: string;
    page?: number;
    size?: number;
  }): Promise<AxiosResponse<OpenapiHostUpgradeRequestListResponse>> =>
    apiClient.client.get('/host/openapi/upgrade-requests', { params }),

  reviewUpgradeRequest: (
    id: string,
    data: OpenapiHostReviewRequest
  ): Promise<AxiosResponse<OpenapiHostUpgradeRequestResponse>> =>
    apiClient.client.put(`/host/openapi/upgrade-requests/${id}`, data),

  getUsage: (params?: {
    from?: string;
    to?: string;
  }): Promise<AxiosResponse<OpenapiHostUsageResponse>> =>
    apiClient.client.get('/host/openapi/usage', { params: params ? { from: params.from ? `${params.from}T00:00:00` : undefined, to: params.to ? `${params.to}T23:59:59.999999` : undefined } : undefined }),

  // Cache cross-tenant (ADR-005)
  listCachedCompanies: (params?: {
    q?: string;
    provincia?: string;
    attivo?: boolean;
    has_visura?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<OpenapiCompanyListResponse>> =>
    apiClient.client.get('/host/openapi/companies', { params }),

  getCachedCompany: (
    id: string
  ): Promise<AxiosResponse<OpenapiCompanyRead>> =>
    apiClient.client.get(`/host/openapi/companies/${id}`),

  refreshCachedCompany: (
    id: string
  ): Promise<AxiosResponse<OpenapiCompanyRead>> =>
    apiClient.client.post(`/host/openapi/companies/${id}/refresh`),

  deleteCachedCompany: (id: string): Promise<AxiosResponse<void>> =>
    apiClient.client.delete(`/host/openapi/companies/${id}`),

  getCacheStats: (params?: {
    window_days?: number;
  }): Promise<AxiosResponse<OpenapiCacheStatsResponse>> =>
    apiClient.client.get('/host/openapi/cache-stats', { params }),

  // Settings host (ADR-006)
  getSettings: (): Promise<AxiosResponse<OpenapiSettingsRead>> =>
    apiClient.client.get('/host/openapi/settings'),

  updateSettings: (
    data: OpenapiSettingsUpdate
  ): Promise<AxiosResponse<OpenapiSettingsRead>> =>
    apiClient.client.put('/host/openapi/settings', data),

  testSettings: (
    data: OpenapiSettingsTestRequest
  ): Promise<AxiosResponse<OpenapiSettingsTestResponse>> =>
    apiClient.client.post('/host/openapi/settings/test', data),
};

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime?: string;
}
export interface DriveImportReport {
  mode: 'data' | 'listino' | 'immagini';
  processed: number;
  matched?: number;
  updated: number;
  created?: number;
  skipped?: number;
  errors?: number;
  images_cached?: number;
  conflicts?: { codice_metel: string; detail: string }[];
}
export interface DriveMapping {
  id: string;
  name: string;
  target: 'clienti' | 'prodotti' | 'prezzi' | 'offerte';
  fields: Record<string, string>;
  keys: Record<string, string>;
  options: Record<string, unknown>;
}

export const driveSyncAPI = {
  mappings: (tenantId: string) => apiClient.client.get<{ mappings: DriveMapping[] }>(`/sync-drive/${tenantId}/mappings`),
  saveMapping: (tenantId: string, mapping: DriveMapping) => apiClient.client.put<DriveMapping>(`/sync-drive/${tenantId}/mappings/${mapping.id}`, mapping),
  preview: (tenantId: string, fileId: string) => apiClient.client.post<{ filename: string; columns: string[]; records: number; sample: Record<string, unknown>[] }>(`/sync-drive/${tenantId}/preview`, { file_id: fileId }),
  connect: (tenantId: string) => apiClient.client.post<{ authorization_url: string }>(`/sync-drive/${tenantId}/oauth/start`),
  complete: (code: string, state: string) => apiClient.client.post<{ tenant_id: string }>('/sync-drive/oauth/callback', { code, state }),
  disconnect: (tenantId: string) => apiClient.client.post(`/sync-drive/${tenantId}/disconnect`),
  folders: (tenantId: string, search: string, pageToken?: string) => apiClient.client.get<{ files: { id: string; name: string }[]; nextPageToken?: string; incompleteSearch?: boolean }>(`/sync-drive/${tenantId}/folders`, { params: { search, page_token: pageToken } }),
  config: (tenantId: string) => apiClient.client.get<{ folder_id: string | null; enabled: boolean; has_credentials: boolean; oauth_available: boolean }>(`/sync-drive/${tenantId}/config`),
  save: (tenantId: string, data: { folder_id: string; enabled: boolean }) => apiClient.client.put(`/sync-drive/${tenantId}/config`, data),
  files: (tenantId: string, folderId?: string) => apiClient.client.get<{ folder_id: string; folder_name: string; files: DriveFile[] }>(`/sync-drive/${tenantId}/files`, { params: { folder_id: folderId } }),
  import: (tenantId: string, data: { file_id: string; mode: 'data' | 'listino' | 'immagini'; query_id?: string; mapping_id?: string }) => apiClient.client.post<{ job_id: string; report: DriveImportReport }>(`/sync-drive/${tenantId}/import`, data, { timeout: 600000 }),
};

export const revisionsAPI = {
  exportCurrent: (id: string) => apiClient.client.post(`/offerte/${id}/document-export`, {}, {timeout: 180000}),
  export: (id: string, revision: string, google: boolean) => apiClient.client.post(`/offerte/${id}/revisions/${revision}/export`, {google}, {timeout: 180000}),
  documents: (id: string, revision: string) => apiClient.client.get<Array<{id: string; tipo: string; url: string; versione: number}>>(`/offerte/${id}/revisions/${revision}/documents`),
  list: (id: string) => apiClient.client.get<Array<{id: string; numero_revisione: number; motivo: string; created_at: string}>>(`/offerte/${id}/revisions`),
  create: (id: string, motivo: string) => apiClient.client.post(`/offerte/${id}/revisions`, { motivo }),
  publish: (id: string) => apiClient.client.post<{versione: number}>(`/templates/${id}/publish`),
};

export const templateImportAPI = {
  docx: (file: File) => { const data = new FormData(); data.append('file', file); return apiClient.client.post('/templates/imports/docx', data, {headers: {'Content-Type': 'multipart/form-data'}}); },
  google: (document_id: string) => apiClient.client.post('/templates/imports/google-docs', {document_id}),
};
